import React, { useState, useMemo, useEffect, useRef } from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIButton from '@/components/shared/UIButton';
import UICheckbox from '@/components/shared/UICheckbox';
import usePropertyPageOptions from '@/hooks/usePropertyPageOptions'; // Renamed import
import UIInputWithLabel from '@/components/shared/UIInputWithLabel';
import UISelectWithLabel from '@/components/shared/UISelectWithLabel';
import UITextareaWithLabel from '@/components/shared/UITextareaWithLabel';
import UIMultiInputWithLabel from '@/components/shared/UIMultiInputWithLabel';
import UIInputPairsWithLabels from '@/components/shared/UIInputPairsWithLabels';
import UICheckboxPairsWithLabels from '@/components/shared/UICheckboxPairsWithLabels';
import UITextareaPairsWithLabels from '@/components/shared/UITextareaPairsWithLabels'; // Updated import
import UICheckboxInputPairsWithLabels from '@/components/shared/UICheckboxInputPairsWithLabels'; // Added import
import UICheckboxWithTextarea from '@/components/shared/UICheckboxWithTextarea'; // Added import
import UICheckboxWithLabeledFields from '@/components/shared/UICheckboxWithLabeledFields'; // Added import
import UIFormMessage from '@/components/shared/UIFormMessage'; // Import UIFormMessage
import { updatePropertyRequest } from '@/api/properties'; // Import updatePropertyRequest
import './EditPropertyInformation.scss';

interface EditPropertyInformationProps {
  property?: any; // Use 'any' for now, or define a more specific IProperty interface if available
  loading?: boolean;
  collapsed?: boolean;
  onToggleCollapse: () => void;
}

interface Option {
  value: string;
  label: string;
}

interface CountryOption extends Option {
  provinces?: Option[];
  states?: Option[];
}

// Helper function to get empty form data for clearing
const getEmptyFormData = () => ({
  propertyName: '',
  country: '',
  province: '',
  city: '',
  streetNumber: '',
  streetName: '',
  postalCode: '',
  municipality: '',
  latitude: '',
  longitude: '',
  constructionYear: '',
  foundation: '',
  floors: '',
  dimension: '',
  basementEnabled: false,
  basementDimension: '',
  basementDescription: '',
  exteriorEnabled: false,
  exteriorDimension: '',
  exteriorZoning: '',
  heatingSystemEnabled: false,
  heatingSystem: '',
  heatingEnergyEnabled: false,
  heatingEnergy: '',
  distinctiveFeaturesEnabled: false,
  distinctiveFeatures: '',
  waterSupplyEnabled: false,
  waterSupply: '',
  hearthStoveEnabled: false,
  hearthStove: '',
  sewageSystemEnabled: false,
  sewageSystem: '',
  restrictionsEnabled: false,
  restrictionsPermissions: '',
});

const EditPropertyInformation: React.FC<EditPropertyInformationProps> = ({
                                                                           property,
                                                                           loading: propertyLoading,
                                                                           collapsed,
                                                                           onToggleCollapse,
                                                                         }) => {
  const { optionsData, isLoading: optionsLoading, error: optionsError } = usePropertyPageOptions('properties'); // Renamed hook usage and destructuring

  // Initialize formData with empty values initially
  const [formData, setFormData] = useState(getEmptyFormData);

  const [isProvinceChanging, setIsProvinceChanging] = useState(false); // State for country change loading
  const provinceMinDisplayTimerRef = useRef<NodeJS.Timeout | null>(null); // Renamed ref for clarity

  const [isSaving, setIsSaving] = useState(false); // State for Save button loading
  const [isClearing, setIsClearing] = useState(false); // State for Clear button loading

  const [successMessage, setSuccessMessage] = useState<string | null>(null); // State for success message
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // State for error message

  // Етап 1: Заповнюємо форму з property (БЕЗ країни та провінції)
  useEffect(() => {
    console.log('EditPropertyInformation: Етап 1 - Заповнення форми з property');
    console.log('EditPropertyInformation: property:', property);

    if (property) {
      const newFormData = {
        propertyName: property?.name || '',
        country: '', // Залишаємо порожнім для наступного етапу
        province: '', // Залишаємо порожнім для наступного етапу
        city: property?.address?.city || '',
        streetNumber: property?.address?.buildingNumber || '',
        streetName: property?.address?.street || '',
        postalCode: property?.address?.postalCode || '',
        municipality: property?.address?.municipality || '',
        latitude: property?.address?.latitude || '',
        longitude: property?.address?.longitude || '',
        constructionYear: property?.constructionYear || '',
        foundation: property?.foundation || '',
        floors: property?.floors || '',
        dimension: property?.dimension || '',
        basementEnabled: property?.hasBasement || false,
        basementDimension: property?.basementDimension || '',
        basementDescription: property?.basementDescription || '',
        exteriorEnabled: property?.hasExterior || false,
        exteriorDimension: property?.exteriorDimension || '',
        exteriorZoning: property?.exteriorZoning || '',
        heatingSystemEnabled: false,
        heatingSystem: '',
        heatingEnergyEnabled: false,
        heatingEnergy: '',
        distinctiveFeaturesEnabled: false,
        distinctiveFeatures: '',
        waterSupplyEnabled: false,
        waterSupply: '',
        hearthStoveEnabled: false,
        hearthStove: '',
        sewageSystemEnabled: false,
        sewageSystem: '',
        restrictionsEnabled: property?.hasRestrictions || false,
        restrictionsPermissions: property?.restrictionsDescription || '',
      };

      setFormData(newFormData);
      console.log('EditPropertyInformation: Етап 1 завершений - форма заповнена без країни/провінції');
    }
  }, [property]);

  // Етап 2: Встановлюємо країну коли є optionsData
  useEffect(() => {
    console.log('EditPropertyInformation: Етап 2 - Встановлення країни');
    console.log('EditPropertyInformation: optionsData:', optionsData);
    console.log('EditPropertyInformation: optionsLoading:', optionsLoading);

    if (property && optionsData && !optionsLoading) {
      const propertyCountryLabel = property.address?.country;
      console.log('EditPropertyInformation: Шукаємо країну:', propertyCountryLabel);

      // Додаємо детальний логінг структури країн
      console.log('EditPropertyInformation: Доступні країни в optionsData:', optionsData.fields.country);
      console.log('EditPropertyInformation: Кількість країн:', optionsData.fields.country?.length);

      if (optionsData.fields.country) {
        optionsData.fields.country.forEach((country: CountryOption, index: number) => {
          console.log(`  Країна ${index}: label="${country.label}", value="${country.value}"`);
        });
      }

      if (propertyCountryLabel) {
        // Спробуємо знайти як за label так і за value
        const matchedCountryOptionByLabel = optionsData.fields.country?.find(
          (c: CountryOption) => c.label === propertyCountryLabel
        );

        const matchedCountryOptionByValue = optionsData.fields.country?.find(
          (c: CountryOption) => c.value === propertyCountryLabel
        );

        // Спробуємо знайти без урахування регістру
        const matchedCountryOptionByLabelCaseInsensitive = optionsData.fields.country?.find(
          (c: CountryOption) => c.label?.toLowerCase() === propertyCountryLabel?.toLowerCase()
        );

        console.log('EditPropertyInformation: Пошук за label:', matchedCountryOptionByLabel);
        console.log('EditPropertyInformation: Пошук за value:', matchedCountryOptionByValue);
        console.log('EditPropertyInformation: Пошук за label (без регістру):', matchedCountryOptionByLabelCaseInsensitive);

        const matchedCountryOption = matchedCountryOptionByLabel ||
          matchedCountryOptionByValue ||
          matchedCountryOptionByLabelCaseInsensitive;

        if (matchedCountryOption) {
          console.log('EditPropertyInformation: Знайдено країну:', matchedCountryOption);
          setFormData(prev => ({
            ...prev,
            country: matchedCountryOption.value
          }));
          console.log('EditPropertyInformation: Етап 2 завершений - країна встановлена');
        } else {
          console.log('EditPropertyInformation: Країну не знайдено:', propertyCountryLabel);
        }
      }
    }
  }, [property, optionsData, optionsLoading]);

  // Етап 3: Встановлюємо провінцію коли країна встановлена
  useEffect(() => {
    console.log('EditPropertyInformation: Етап 3 - Встановлення провінції');
    console.log('EditPropertyInformation: formData.country:', formData.country);

    if (property && optionsData && formData.country) {
      const propertyProvinceLabel = property.address?.stateOrProvince;
      console.log('EditPropertyInformation: Шукаємо провінцію:', propertyProvinceLabel);

      if (propertyProvinceLabel) {
        // Знаходимо країну для отримання списку провінцій
        const countryOption = optionsData.fields.country.find(
          (c: CountryOption) => c.value === formData.country
        );

        if (countryOption) {
          const provinceOptionsForCountry = countryOption.provinces || countryOption.states || [];
          console.log('EditPropertyInformation: Провінції для країни:', provinceOptionsForCountry);
          console.log('EditPropertyInformation: Кількість провінцій:', provinceOptionsForCountry.length);

          // Додаємо детальний логінг структури провінцій
          if (provinceOptionsForCountry.length > 0) {
            provinceOptionsForCountry.forEach((province: Option, index: number) => {
              console.log(`  Провінція ${index}: label="${province.label}", value="${province.value}"`);
            });
          }

          // Спробуємо знайти як за label так і за value
          const matchedProvinceOptionByLabel = provinceOptionsForCountry.find(
            (p: Option) => p.label === propertyProvinceLabel
          );

          const matchedProvinceOptionByValue = provinceOptionsForCountry.find(
            (p: Option) => p.value === propertyProvinceLabel
          );

          // Спробуємо знайти без урахування регістру
          const matchedProvinceOptionByLabelCaseInsensitive = provinceOptionsForCountry.find(
            (p: Option) => p.label?.toLowerCase() === propertyProvinceLabel?.toLowerCase()
          );

          console.log('EditPropertyInformation: Пошук провінції за label:', matchedProvinceOptionByLabel);
          console.log('EditPropertyInformation: Пошук провінції за value:', matchedProvinceOptionByValue);
          console.log('EditPropertyInformation: Пошук провінції за label (без регістру):', matchedProvinceOptionByLabelCaseInsensitive);

          const matchedProvinceOption = matchedProvinceOptionByLabel ||
            matchedProvinceOptionByValue ||
            matchedProvinceOptionByLabelCaseInsensitive;

          if (matchedProvinceOption) {
            console.log('EditPropertyInformation: Знайдено провінцію:', matchedProvinceOption);
            setFormData(prev => ({
              ...prev,
              province: matchedProvinceOption.value
            }));
            console.log('EditPropertyInformation: Етап 3 завершений - провінція встановлена');
          } else {
            console.log('EditPropertyInformation: Провінцію не знайдено:', propertyProvinceLabel);
          }
        }
      }
    }
  }, [property, optionsData, formData.country]);

  // Керування спінером провінцій
  useEffect(() => {
    console.log('EditPropertyInformation: Управління спінером провінцій');
    console.log('EditPropertyInformation: optionsLoading:', optionsLoading, 'formData.country:', formData.country);

    // Якщо завантажуються опції або немає обраної країни - показуємо спінер
    if (optionsLoading || !formData.country) {
      if (!isProvinceChanging) {
        setIsProvinceChanging(true);
        console.log('EditPropertyInformation: Показуємо спінер провінцій');
      }
      return;
    }

    // Якщо опції завантажені і країна обрана - ховаємо спінер після 500мс
    if (!optionsLoading && formData.country) {
      if (provinceMinDisplayTimerRef.current) {
        clearTimeout(provinceMinDisplayTimerRef.current);
      }

      provinceMinDisplayTimerRef.current = setTimeout(() => {
        setIsProvinceChanging(false);
        console.log('EditPropertyInformation: Ховаємо спінер провінцій після 500мс');
      }, 500);
    }

    // Очистка таймера
    return () => {
      if (provinceMinDisplayTimerRef.current) {
        clearTimeout(provinceMinDisplayTimerRef.current);
      }
    };
  }, [optionsLoading, formData.country]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => { // Made async
    if (!property?.id) {
      setErrorMessage('Property ID is missing. Cannot save.');
      return;
    }

    setIsSaving(true);
    setSuccessMessage(null); // Clear previous messages
    setErrorMessage(null); // Clear previous messages

    try {
      const dataToSave = {
        name: formData.propertyName,
        constructionYear: parseInt(formData.constructionYear as string, 10) || null,
        foundation: formData.foundation,
        floors: parseInt(formData.floors as string, 10) || null,
        dimension: parseInt(formData.dimension as string, 10) || null,
        hasBasement: formData.basementEnabled,
        basementDimension: formData.basementDimension,
        basementDescription: formData.basementDescription,
        hasExterior: formData.exteriorEnabled,
        exteriorDimension: formData.exteriorDimension,
        exteriorZoning: formData.exteriorZoning,
        hasRestrictions: formData.restrictionsEnabled,
        restrictionsDescription: formData.restrictionsPermissions,
        address: {
          country: formData.country,
          stateOrProvince: formData.province,
          city: formData.city,
          street: formData.streetName,
          buildingNumber: formData.streetNumber, // Corrected to streetNumber
          municipality: formData.municipality,
          postalCode: formData.postalCode,
          latitude: parseFloat(formData.latitude as string) || null,
          longitude: parseFloat(formData.longitude as string) || null,
        },
      };

      const response = await updatePropertyRequest({ id: property.id, data: dataToSave });
      setSuccessMessage('Property information saved successfully!');
      // Optionally update formData with response data if server returns updated object
      // setFormData(getInitialFormData(response.data));
    } catch (error: any) {
      let messageToDisplay = 'Failed to save property information. Please try again.';

      if (error.response && error.response.status === 400 && error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const fieldErrors = Object.keys(validationErrors)
          .map(field => {
            const errors = validationErrors[field].join(', ');
            return `- ${field}: ${errors}`;
          })
          .join('\n');
        messageToDisplay = `${error.response.data.message || 'Validation failed'}:\n${fieldErrors}`;
      } else if (error.response?.data?.message) {
        messageToDisplay = error.response.data.message;
      } else if (error.message) {
        messageToDisplay = error.message;
      }
      setErrorMessage(messageToDisplay);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsClearing(true);
    setSuccessMessage(null); // Clear previous messages
    setErrorMessage(null); // Clear previous messages
    // Simulate clearing process
    setTimeout(() => {
      setFormData(getEmptyFormData()); // Reset form to empty values
      setIsClearing(false);
    }, 500); // Show spinner for 0.5 seconds
  };

  // Function to clear messages when UIFormMessage closes
  const handleMessageClose = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const allCountryOptions: CountryOption[] = useMemo(() => {
    console.log('EditPropertyInformation: allCountryOptions useMemo re-evaluating');
    if (optionsData && optionsData.fields && optionsData.fields.country) {
      const options = optionsData.fields.country.map((country: any) => ({
        value: country.value,
        label: country.label,
        provinces: country.provinces || country.states, // Handle both 'provinces' and 'states'
      }));
      console.log('EditPropertyInformation: allCountryOptions:', options);
      return options;
    }
    console.log('EditPropertyInformation: allCountryOptions: [] (optionsData not available)');
    return [];
  }, [optionsData]);

  const countryOptions = useMemo(() => {
    console.log('EditPropertyInformation: countryOptions useMemo re-evaluating');
    const options = allCountryOptions.map(country => ({ value: country.value, label: country.label }));
    console.log('EditPropertyInformation: countryOptions:', options);
    return options;
  }, [allCountryOptions]);

  const provinceOptions: Option[] = useMemo(() => {
    console.log('EditPropertyInformation: provinceOptions useMemo re-evaluating');
    console.log('EditPropertyInformation: formData.country for provinceOptions:', formData.country);
    const selectedCountry = allCountryOptions.find(
      (country) => country.value === formData.country
    );
    console.log('EditPropertyInformation: selectedCountry for provinceOptions:', selectedCountry);

    const options = (selectedCountry?.provinces || selectedCountry?.states || []);
    console.log('EditPropertyInformation: provinceOptions:', options);
    return options;
  }, [formData.country, allCountryOptions]);

  const handleCountryChange = (value: string) => {
    console.log('EditPropertyInformation: handleCountryChange called with value:', value);
    setFormData((prev) => ({
      ...prev,
      country: value,
      province: '', // Reset province when country changes
    }));

    // Immediately show spinner, the useEffect above will manage hiding it
    setIsProvinceChanging(true);
    console.log('EditPropertyInformation: Setting isProvinceChanging to true on country change');
  };

  const isLoading = propertyLoading || optionsLoading;

  const addressInputs = [
    {
      value: formData.streetNumber,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('streetNumber', e.target.value),
      placeholder: 'Building Number',
    },
    {
      value: formData.streetName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('streetName', e.target.value),
      placeholder: 'Street Name',
    },
  ];

  const geolocationPairs = [
    {
      label: 'Latitude',
      value: formData.latitude,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('latitude', e.target.value),
      placeholder: 'Latitude',
    },
    {
      label: 'Longitude',
      value: formData.longitude,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('longitude', e.target.value),
      placeholder: 'Longitude',
    },
  ];

  const constructionDetailsPairs = [
    {
      label: 'Construction year',
      value: formData.constructionYear,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('constructionYear', e.target.value),
      placeholder: 'Construction year',
    },
    {
      label: 'Foundation',
      value: formData.foundation,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('foundation', e.target.value),
      placeholder: 'Foundation',
    },
  ];

  const floorDimensionPairs = [
    {
      label: 'Floors',
      value: formData.floors,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('floors', e.target.value),
      placeholder: 'Number of floors',
    },
    {
      label: 'Dimension',
      value: formData.dimension,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('dimension', e.target.value),
      placeholder: 'Dimension',
    },
  ];

  const otherCharacteristicsPairs = useMemo(() => [
    {
      checkboxLabel: 'Heating system',
      checked: formData.heatingSystemEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('heatingSystemEnabled', e.target.checked),
      inputValue: formData.heatingSystem,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('heatingSystem', e.target.value),
      inputPlaceholder: 'Enter...',
    },
    {
      checkboxLabel: 'Water supply',
      checked: formData.waterSupplyEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('waterSupplyEnabled', e.target.checked),
      inputValue: formData.waterSupply,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('waterSupply', e.target.value),
      inputPlaceholder: 'Enter...',
    },
    {
      checkboxLabel: 'Heating energy',
      checked: formData.heatingEnergyEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('heatingEnergyEnabled', e.target.checked),
      inputValue: formData.heatingEnergy,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('heatingEnergy', e.target.value),
      inputPlaceholder: 'Enter...',
    },
    {
      checkboxLabel: 'Hearth stove',
      checked: formData.hearthStoveEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('hearthStoveEnabled', e.target.checked),
      inputValue: formData.hearthStove,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('hearthStove', e.target.value),
      inputPlaceholder: 'Enter...',
    },
    {
      checkboxLabel: 'Distinctive features',
      checked: formData.distinctiveFeaturesEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('distinctiveFeaturesEnabled', e.target.checked),
      inputValue: formData.distinctiveFeatures,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('distinctiveFeatures', e.target.value),
      inputPlaceholder: 'Enter...',
    },
    {
      checkboxLabel: 'Sewage system',
      checked: formData.sewageSystemEnabled,
      onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('sewageSystemEnabled', e.target.checked),
      inputValue: formData.sewageSystem,
      onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('sewageSystem', e.target.value),
      inputPlaceholder: 'Enter...',
    },
  ], [formData]);

  // The final loading state for the Province select
  // It's loading if overall options are loading OR if isProvinceChanging is true
  const isProvinceSelectLoading = optionsLoading || isProvinceChanging;

  return (
    <UICollapsibleSection
      title="Property information"
      description="" // Changed to empty string
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={isLoading}
      loadingText="Loading property information..."
      className="EditPropertyInformation"
    >
      <div className="EditPropertyInformation__form">
        {/* First form section */}
        <div className="EditPropertyInformation__form-section">
          <UIInputWithLabel
            label="Property name"
            value={formData.propertyName}
            onChange={(e) =>
              handleInputChange('propertyName', e.target.value)
            }
            placeholder="Property name"
            fullWidth
          />

          <div className="EditPropertyInformation__caption">Address</div> {/* Added Address caption */}

          <UISelectWithLabel
            label="Country"
            value={formData.country}
            onChange={(e) => handleCountryChange(e.target.value)}
            options={countryOptions}
            placeholder="Select country"
            fullWidth
          />

          <UISelectWithLabel
            label="Province"
            value={formData.province}
            onChange={(e) => handleInputChange('province', e.target.value)}
            options={provinceOptions}
            placeholder="Select province"
            fullWidth
            disabled={!formData.country || provinceOptions.length === 0} // Disable if no country selected or no provinces
            loading={isProvinceSelectLoading} // Pass the combined loading state
          />

          <UIInputWithLabel
            label="City"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="City"
            fullWidth
          />

          <UIMultiInputWithLabel
            label="Address"
            labelWidth={25}
            inputs={addressInputs}
            inputWidths={[20, 60]}
          />

          <UIInputWithLabel
            label="Municipality"
            value={formData.municipality}
            onChange={(e) =>
              handleInputChange('municipality', e.target.value)
            }
            placeholder="Municipality"
            fullWidth
          />

          <UIInputWithLabel
            label="Postal code"
            value={formData.postalCode}
            onChange={(e) => handleInputChange('postal code', e.target.value)}
            placeholder="Postal code"
            fullWidth
          />
        </div>

        {/* Geolocation section */}
        <div className="EditPropertyInformation__caption">Geolocation</div> {/* Added Geolocation caption */}
        <UIInputPairsWithLabels
          pairs={geolocationPairs}
          labelWidth={25}
        />

        {/* Construction and building details */}
        <div className="EditPropertyInformation__caption">Main information</div> {/* Added Main information caption */}
        <div className="EditPropertyInformation__form-section-pair">
          <UIInputPairsWithLabels
            pairs={constructionDetailsPairs}
            labelWidth={25}
          />
          <UIInputPairsWithLabels
            pairs={floorDimensionPairs}
            labelWidth={25}
          />
        </div>

        {/* Basement and Exterior sections */}
        <div className="EditPropertyInformation__indent"> {/* Added indent div */}
          <div className="EditPropertyInformation__form-section-pair">
            <UICheckboxWithLabeledFields
              checkboxLabel="Basement"
              checked={formData.basementEnabled}
              onCheckboxChange={(e) => handleInputChange('basementEnabled', e.target.checked)}
              inputLabel="Dimension"
              inputValue={formData.basementDimension}
              onInputChange={(e) => handleInputChange('basementDimension', e.target.value)}
              inputPlaceholder="Dimension"
              textareaLabel="Description"
              textareaValue={formData.basementDescription}
              onTextareaChange={(e) => handleInputChange('basementDescription', e.target.value)}
              textareaPlaceholder="Enter..."
              labelWidth={25}
            />
            <UICheckboxWithLabeledFields
              checkboxLabel="Exterior"
              checked={formData.exteriorEnabled}
              onCheckboxChange={(e) => handleInputChange('exteriorEnabled', e.target.checked)}
              inputLabel="Dimension"
              inputValue={formData.exteriorDimension}
              onInputChange={(e) => handleInputChange('exteriorDimension', e.target.value)}
              inputPlaceholder="Dimension"
              textareaLabel="Zoning"
              textareaValue={formData.exteriorZoning}
              onTextareaChange={(e) => handleInputChange('exteriorZoning', e.target.value)}
              textareaPlaceholder="Enter..."
              labelWidth={25}
            />
          </div>
        </div>

        {/* Other characteristics */}
        {/* <div className="EditPropertyInformation__form-section">
          <div className="EditPropertyInformation__caption">Other characteristics</div>
          <div className="EditPropertyInformation__column-group">
            <UICheckboxInputPairsWithLabels pairs={otherCharacteristicsPairs.slice(0, 2)} checkboxWidth={25} />
            <UICheckboxInputPairsWithLabels pairs={otherCharacteristicsPairs.slice(2, 4)} checkboxWidth={25} />
            <UICheckboxInputPairsWithLabels pairs={otherCharacteristicsPairs.slice(4, 6)} checkboxWidth={25} />
          </div>
        </div> */}

        {/* Restrictions/Permissions */}
        <div className="EditPropertyInformation__form-section">
          <UICheckboxWithTextarea
            checkboxLabel="Restrictions/Permissions"
            checked={formData.restrictionsEnabled}
            onCheckboxChange={(e) => handleInputChange('restrictionsEnabled', e.target.checked)}
            textareaValue={formData.restrictionsPermissions}
            onTextareaChange={(e) => handleInputChange('restrictionsPermissions', e.target.value)}
            textareaPlaceholder="Enter..."
            checkboxWidth={25}
          />
        </div>

        {/* Messages */}
        {(successMessage || errorMessage) && (
          <UIFormMessage
            type={successMessage ? 'success' : 'error'}
            message={successMessage || errorMessage || ''}
            onClose={handleMessageClose} // Pass the new onClose handler
          />
        )}

        {/* Action buttons */}
        <div className="EditPropertyInformation__buttons">
          <UIButton variant="primary" onClick={handleSave} loading={isSaving}>
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleCancel} loading={isClearing}>
            Clear
          </UIButton>
        </div>
      </div>
    </UICollapsibleSection>
  );
};

export default EditPropertyInformation;